import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { CalendarClock, Send } from 'lucide-react';
import { resolveTimeZone, getTimezoneAbbreviation, describeTzSpec } from '../utils/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROWS = [
  { key: 'certify', label: 'Workers certify their shifts by', hint: 'Workers get a bell + email nudge 24 h before this listing anything uncertified.' },
  { key: 'approve', label: 'Supervisors approve by', hint: 'Return anything wrong before this so the worker can fix and re-certify.' },
  { key: 'lock', label: 'Admin locks & pays by', hint: 'Lock the period, then push to QuickBooks or export the CSV.' },
];

const fmtLocal = (iso, tz) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const zone = resolveTimeZone(tz, d);
  return `${d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(zone ? { timeZone: zone } : {}) })}${tz ? ` ${getTimezoneAbbreviation(tz, d)}` : ''}`;
};

export default function PayrollCutoffsCard({ token }) {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => axios.get(`${API}/company/payroll-cutoffs`, auth).then((r) => { setData(r.data); setForm(r.data.cutoffs); }).catch(() => {});
  useEffect(() => { if (token) load(); }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/company/payroll-cutoffs`, form, auth);
      setData(r.data); setForm(r.data.cutoffs);
      toast({ title: 'Payroll cut-offs saved', description: 'The manual timeline and certify reminders now use these deadlines.' });
    } catch (e) {
      toast({ title: 'Could not save', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const sendNow = async (p) => {
    if (!p) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/payroll/certify-reminders/send-now`, { start_date: p.start, end_date: p.end }, auth);
      toast({ title: `Reminders sent to ${r.data.reminders_sent} worker${r.data.reminders_sent === 1 ? '' : 's'}`, description: `Everyone with uncertified shifts in ${p.start} → ${p.end}.` });
    } catch (e) {
      toast({ title: 'Could not send', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSending(false); }
  };

  if (!form) return null;
  const cur = data?.current;

  return (
    <Card className="shadow-lg" data-testid="payroll-cutoffs-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-blue-600" /> Payroll cut-offs</CardTitle>
        <p className="text-sm text-gray-500">Deadlines counted from the end of each pay period (company time zone). Used by the Timekeeping &amp; Payroll guide and the certify reminders.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        {!data?.pay_period_configured && (
          <p className="text-xs rounded-lg px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200" data-testid="cutoffs-no-pay-period">
            No pay-period cycle is configured yet (Reports → Payroll → Pay Period). Cut-offs save fine, but deadlines can only be computed once the cycle exists.
          </p>
        )}
        {ROWS.map((r) => (
          <div key={r.key} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_130px] gap-3 items-end" data-testid={`cutoff-row-${r.key}`}>
            <div>
              <Label>{r.label}</Label>
              <p className="text-xs text-gray-500 mt-0.5">{r.hint}</p>
              {cur && <p className="text-xs text-blue-700 mt-1" data-testid={`cutoff-deadline-${r.key}`}>This period: {fmtLocal(cur.deadlines?.[r.key], data?.timezone)} ({describeTzSpec(data?.timezone)})</p>}
            </div>
            <div>
              <Label className="text-xs">Days after period end</Label>
              <Input type="number" min={0} max={21} value={form[`${r.key}_days`]}
                onChange={(e) => setForm({ ...form, [`${r.key}_days`]: Number(e.target.value) })} data-testid={`cutoff-${r.key}-days`} />
            </div>
            <div>
              <Label className="text-xs">Time</Label>
              <Input type="time" value={form[`${r.key}_time`]}
                onChange={(e) => setForm({ ...form, [`${r.key}_time`]: e.target.value })} data-testid={`cutoff-${r.key}-time`} />
            </div>
          </div>
        ))}
        <Button onClick={save} disabled={saving} className="w-full" data-testid="cutoffs-save-btn">{saving ? 'Saving…' : 'Save cut-offs'}</Button>
        <div className="border-t pt-4 flex flex-wrap items-center gap-2">
          <p className="text-xs text-gray-600 flex-1 min-w-[200px]">Need it now? Nudge everyone with uncertified shifts (bell + email) for a period.</p>
          <Button size="sm" variant="outline" disabled={sending || !cur} onClick={() => sendNow(cur)} data-testid="cutoffs-send-now-current">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Current period
          </Button>
          <Button size="sm" variant="outline" disabled={sending || !data?.previous} onClick={() => sendNow(data?.previous)} data-testid="cutoffs-send-now-previous">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Previous period
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
